import { getLayout } from './utils/layout';
import _ from 'lodash';
import experiment from 'experiment';

import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef, CompLayout } from 'types/documentServices';
import type { Gap } from './resizeGapsTypes';

const GAP_MIN_HEIGHT = 10;
const CONTAINER_MIN_WIDTH = 120; // empirically chosen value

export const findGapUnderCursor = (editorAPI: EditorAPI): Gap | null => {
  const selectedComps = editorAPI.selection.getSelectedComponents();
  if (selectedComps.length !== 1) {
    return null;
  }
  const selectedComp = selectedComps[0];

  const isInsideHeaderFooter = experiment.isOpen('se_resizeGapsInFooter')
    ? isHeaderOrItsDescendant(editorAPI, selectedComp)
    : isHeaderFooterOrItsDescendant(editorAPI, selectedComp);
  if (isInsideHeaderFooter) {
    return null;
  }

  const nonPageAncestors = editorAPI.components
    .getAncestors(selectedComp)
    .filter((comp) => !editorAPI.components.is.page(comp));
  if (isInsideTPAWidget(editorAPI, nonPageAncestors)) {
    return null;
  }

  const topNonPageAncestor = nonPageAncestors[nonPageAncestors.length - 1];
  if (!isCursorWithinComponent(editorAPI, topNonPageAncestor || selectedComp)) {
    return;
  }

  const getLayoutMemoized = _.memoize((compRef: CompRef) =>
    getLayout(editorAPI, compRef),
  );

  for (const compRef of [selectedComp, ...nonPageAncestors]) {
    if (isCursorWithinComponent(editorAPI, compRef)) {
      return tryFindGapInsideComp(editorAPI, compRef, getLayoutMemoized);
    }
  }
};

function tryFindGapInsideComp(
  editorAPI: EditorAPI,
  compRef: CompRef,
  getLayout: (compRef: CompRef) => CompLayout,
): Gap | null {
  const { y: mouseY } = editorAPI.cursor.get();

  const container = getGapContainerInsideComp(editorAPI, compRef);
  if (!container) {
    return null;
  }

  const children = getVisibleAndSelectableChildren(
    editorAPI,
    container,
    getLayout,
  );

  if (!children.length) {
    return null;
  }

  if (!isContainerWideEnough(container, getLayout)) {
    return null;
  }

  let gapTop = getLayout(container).y;
  let gapBottom = getLayout(container).y + getLayout(container).height;
  let componentBelow: CompRef | null = null;
  let componentAbove: CompRef | null = null;

  for (const child of children) {
    const childTop = getLayout(child).y;
    const childBottom = getLayout(child).y + getLayout(child).height;

    if (mouseY >= childTop && mouseY <= childBottom) {
      // mouse is either inside component or in the horizontal gap,
      // but not close to edge of the comp
      return null;
    }

    if (mouseY > childBottom && childBottom > gapTop) {
      gapTop = childBottom;
      componentAbove = child;
    }

    if (mouseY < childTop && childTop < gapBottom) {
      gapBottom = childTop;
      componentBelow = child;
    }
  }

  const gap = {
    container,
    componentAbove,
    componentBelow,
    layout: {
      y: gapTop,
      x: getLayout(container).x,
      width: getLayout(container).width,
      height: gapBottom - gapTop,
    },
  };

  if (gap.layout.height < GAP_MIN_HEIGHT) {
    return null;
  }

  return gap;
}

/**
 * On small components, where children take a lot of space of the container component,
 * it may be hard for user to find the space for drag and drop the component itself. If
 * we highlight gaps in such components, we make it even harder to find that space, because
 * gap handle will "eat" all "mousedown+mousemove"-s.
 */
function isContainerWideEnough(
  container: CompRef,
  getLayout: (compRef: CompRef) => CompLayout,
) {
  return getLayout(container).width >= CONTAINER_MIN_WIDTH;
}

function isHeaderOrItsDescendant(editorAPI: EditorAPI, comp: CompRef) {
  return (
    editorAPI.sections.isHeader(comp) ||
    editorAPI.sections.isHeader(editorAPI.sections.getClosestHeaderFooter(comp))
  );
}

function isHeaderFooterOrItsDescendant(editorAPI: EditorAPI, comp: CompRef) {
  return (
    editorAPI.sections.isHeaderOrFooter(comp) ||
    !!editorAPI.sections.getClosestHeaderFooter(comp)
  );
}

function isCursorWithinComponent(editorAPI: EditorAPI, comp: CompRef) {
  const { x, y } = editorAPI.cursor.get();
  const layout = getLayout(editorAPI, comp);

  return (
    x >= layout.x &&
    y >= layout.y &&
    x <= layout.x + layout.width &&
    y <= layout.y + layout.height
  );
}

function isInsideTPAWidget(editorAPI: EditorAPI, ancestorsCompRefs: CompRef[]) {
  return ancestorsCompRefs.some(
    (compRef) =>
      editorAPI.components.getType(compRef) === 'platform.components.AppWidget',
  );
}

function getGapContainerInsideComp(
  editorAPI: EditorAPI,
  comp: CompRef,
): CompRef | null {
  const compType = editorAPI.components.getType(comp);
  const validContainerTypes = [
    'mobile.core.components.Container',
    'wysiwyg.viewer.components.Column',
    'wysiwyg.viewer.components.ClassicSection',
    'wysiwyg.viewer.components.StripColumnsContainer',
  ];

  if (experiment.isOpen('se_resizeGapsInFooter')) {
    validContainerTypes.push('wysiwyg.viewer.components.FooterContainer');
  }

  if (!validContainerTypes.includes(compType)) {
    return null;
  }

  if (compType === 'wysiwyg.viewer.components.StripColumnsContainer') {
    // When a strip has only one column, the column
    // itself can't be selected (only the whole strip is selected),
    // however, it still has children, and it should serve as a container
    // for the gap.
    const children = editorAPI.components.getChildren(comp);
    if (children.length === 1) {
      return children[0];
    }
  }

  return comp;
}

function getVisibleAndSelectableChildren(
  editorAPI: EditorAPI,
  compRef: CompRef,
  getLayout: (compRef: CompRef) => CompLayout,
) {
  return editorAPI.components
    .getChildren(compRef)
    .filter(editorAPI.components.is.selectable)
    .filter(
      (child: CompRef) =>
        getLayout(child).width > 0 && getLayout(child).height > 0,
    );
}
